import type { ReactElement } from "react"
import { ArrowDownward, ArrowUpward } from "@mui/icons-material"
import { useTheme } from "@mui/material"
import intl from "src/internationalization/intl"
import { BoxCard, Grid, IconWrapper, Sparkline, Typography } from "src/lib"
import { CARD_WIDTH } from "src/lib/consts/cardSize"

interface OverviewMetricCardProps {
    icon: ReactElement
    accentColor: string
    accentSlot: "primary" | "secondary" | "tertiary" | "quaternary"
    label: string
    valueText: string
    /** Delta bruto (na unidade própria da métrica: p.p. para taxas, % para as demais). Null quando não há período anterior para comparar. */
    deltaValue: number | null
    /** Formata o módulo do delta para exibição (ex.: "3 p.p.", "12,3%"). */
    deltaFormat: (value: number) => string
    /** Direção da variação que representa uma melhora para esta métrica (define a cor do delta). */
    favorableDirection: "up" | "down"
    sparklinePoints: { date: string; value: number }[]
    selected: boolean
    onSelect: () => void
    /** Ex.: botão de filtro (Tune) ao lado do título — não deve disparar onSelect ao clicar. */
    headerAction?: ReactElement
    /** Ex.: campos de filtro colapsáveis, abaixo do sparkline — não deve disparar onSelect ao clicar. */
    footer?: ReactElement
}

const OverviewMetricCard = ({
    icon,
    accentColor,
    accentSlot,
    label,
    valueText,
    deltaValue,
    deltaFormat,
    favorableDirection,
    sparklinePoints,
    selected,
    onSelect,
    headerAction,
    footer,
}: OverviewMetricCardProps) => {
    const theme = useTheme() as any

    const hasDelta = deltaValue !== null
    const isIncrease = hasDelta && (deltaValue as number) >= 0
    const isFavorable = hasDelta && isIncrease === (favorableDirection === "up")
    const deltaColor = !hasDelta
        ? theme.palette.neutral.main
        : isFavorable
          ? theme.palette.accentSuccess.main
          : theme.palette.accentError.main

    return (
        <BoxCard
            onClick={onSelect}
            role="button"
            tabIndex={0}
            aria-pressed={selected}
            onKeyDown={(e: React.KeyboardEvent) => {
                if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault()
                    onSelect()
                }
            }}
            sx={{
                width: CARD_WIDTH,
                maxWidth: CARD_WIDTH,
                p: 2,
                cursor: "pointer",
                border: `1px solid ${selected ? accentColor : "rgba(255, 255, 255, 0.06)"}`,
                transition: "border-color 120ms ease",
                "&:focus-visible": { outline: `2px solid ${accentColor}`, outlineOffset: 2 },
            }}
        >
            <Grid container gap={1} justifyContent="space-between" alignItems="center" wrap="nowrap">
                <Grid item sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
                    <IconWrapper color={accentSlot}>{icon}</IconWrapper>
                    <Typography variant="h5" noWrap>
                        {label}
                    </Typography>
                </Grid>
                {headerAction && (
                    <Grid item onClick={(e) => e.stopPropagation()}>
                        {headerAction}
                    </Grid>
                )}
            </Grid>
            <Typography sx={{ fontSize: 22, fontWeight: 700, mt: 1.5, color: "neutral.lighter" }}>{valueText}</Typography>
            <Grid container alignItems="center" gap={0.5} sx={{ mt: 0.5, minHeight: 20 }}>
                {hasDelta && (
                    <Grid container alignItems="center" wrap="nowrap" sx={{ width: "auto", color: deltaColor }}>
                        {isIncrease ? <ArrowUpward sx={{ fontSize: 14 }} /> : <ArrowDownward sx={{ fontSize: 14 }} />}
                        <Typography variant="caption" sx={{ color: deltaColor, fontWeight: 700, ml: 0.25 }}>
                            {deltaFormat(Math.abs(deltaValue as number))}
                        </Typography>
                    </Grid>
                )}
                <Typography variant="caption" color="quaternary">
                    {intl("vsPreviousPeriod")}
                </Typography>
            </Grid>
            <Grid sx={{ mt: 1.5 }}>
                <Sparkline points={sparklinePoints} color={accentColor} label={label} />
            </Grid>
            {footer && (
                <div onClick={(e) => e.stopPropagation()}>
                    {footer}
                </div>
            )}
        </BoxCard>
    )
}

export { OverviewMetricCard }
